// src/pages/Admin/StudentView.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import PaginatedTable from '../../components/PaginatedTable';
import TitleWithBack from '../../components/common/TitleWithBack';

function StudentView() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [examCentres, setExamCentres] = useState([]);
  const [years, setYears] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tooltip, setTooltip] = useState('');
  const [filters, setFilters] = useState({
    query: '',
    programId: '',
    examCentreId: '',
    yearId: '',
    semesterId: '',
    status: ''
  });
  const [sortKey, setSortKey] = useState('id');
  const [sortOrder, setSortOrder] = useState('desc');
  const [confirmDeleteStudent, setConfirmDeleteStudent] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  async function fetchAllData() {
    setLoading(true);
    try {
      const [stuRes, progRes, centreRes, yearRes, semRes] = await Promise.all([
        axios.get('/api/students'),
        axios.get('/api/programs'),
        axios.get('/api/exam-centres'),
        axios.get('/api/years-of-study'),
        axios.get('/api/semesters')
      ]);
      const stuArray = Array.isArray(stuRes.data)
        ? stuRes.data
        : (stuRes.data.data || []);
      stuArray.sort((a, b) => b.id - a.id);
      setStudents(stuArray);
      setPrograms(Array.isArray(progRes.data) ? progRes.data : (progRes.data.data || []));
      setExamCentres(Array.isArray(centreRes.data) ? centreRes.data : (centreRes.data.data || []));
      setYears(Array.isArray(yearRes.data) ? yearRes.data : (yearRes.data.data || []));
      setSemesters(Array.isArray(semRes.data) ? semRes.data : (semRes.data.data || []));
    } catch (err) {
      setError('Error fetching students or filter lists.');
    }
    setLoading(false);
  }

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  const getYearName = (stuYear) => {
    if (!stuYear) return '';
    if (typeof stuYear === 'object') return stuYear.name || '';
    const found = years.find(y => y.id === parseInt(stuYear, 10));
    return found ? found.name : stuYear;
  };

  const getSemesterName = (stuSem) => {
    if (!stuSem) return '';
    if (typeof stuSem === 'object') return stuSem.name || '';
    const found = semesters.find(s => s.id === parseInt(stuSem, 10));
    return found ? found.name : stuSem;
  };

  const filteredStudents = students.filter(stu => {
    const query = filters.query.toLowerCase().trim();
    const queryMatch = !query ||
      (stu.name && stu.name.toLowerCase().includes(query)) ||
      (stu.email && stu.email.toLowerCase().includes(query))||
      (stu.registrationNumber && stu.registrationNumber.toLowerCase().includes(query));
    const programMatch = filters.programId ? stu.program?.id?.toString() === filters.programId : true;
    const centreMatch = filters.examCentreId ? stu.examCenter?.id?.toString() === filters.examCentreId : true;
    const yearMatch = filters.yearId ? stu.yearOfStudy?.id?.toString() === filters.yearId : true;
    const semesterMatch = filters.semesterId ? stu.semester?.id?.toString() === filters.semesterId : true;
    const statusMatch = filters.status ? stu.status?.toLowerCase() === filters.status.toLowerCase() : true;
    return queryMatch && programMatch && centreMatch && yearMatch && semesterMatch && statusMatch;
  });

  const sortedStudents = [...filteredStudents].sort((a, b) => {
    let aVal = a[sortKey] || '';
    let bVal = b[sortKey] || '';
    if (sortKey === 'name') {
      aVal = a.name || '';
      bVal = b.name || '';
    } else if (sortKey === 'email') {
      aVal = a.email || '';
      bVal = b.email || '';
    } else if (sortKey === 'programName') {
      aVal = a.program?.name || '';
      bVal = b.program?.name || '';
    } else if (sortKey === 'examCentreName') {
      aVal = a.examCenter?.centreName || '';
      bVal = b.examCenter?.centreName || '';
    } else if (sortKey === 'yearOfStudy') {
      aVal = a.yearOfStudy?.name || '';
      bVal = b.yearOfStudy?.name || '';
    } else if (sortKey === 'semester') {
      aVal = a.semester?.name || '';
      bVal = b.semester?.name || '';
    } else if (sortKey === 'status') {
      aVal = a.status || '';
      bVal = b.status || '';
    }
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    } else {
      return sortOrder === 'asc'
        ? aVal.toString().localeCompare(bVal.toString())
        : bVal.toString().localeCompare(aVal.toString());
    }
  });

  const columns = [
    { key: 'serial', header: <>#</> },
    { key: 'name', header: <span onClick={() => handleSort('name')} style={{cursor: 'pointer'}}>Name {sortKey === 'name' && (sortOrder === 'asc' ? '\u25b2' : '\u25bc')}</span> },
    { key: 'email', header: <span onClick={() => handleSort('email')} style={{cursor: 'pointer'}}>Email {sortKey === 'email' && (sortOrder === 'asc' ? '\u25b2' : '\u25bc')}</span> },
    { key: 'registrationNumber', header: <span onClick={() => handleSort('registrationNumber')} style={{cursor: 'pointer'}}>Registration Number {sortKey === 'registrationNumber' && (sortOrder === 'asc' ? '\u25b2' : '\u25bc')}</span> },
    { key: 'programName', header: <span onClick={() => handleSort('programName')} style={{cursor: 'pointer'}}>Program {sortKey === 'programName' && (sortOrder === 'asc' ? '\u25b2' : '\u25bc')}</span> },
    { key: 'examCentreName', header: <span onClick={() => handleSort('examCentreName')} style={{cursor: 'pointer'}}>Exam Centre {sortKey === 'examCentreName' && (sortOrder === 'asc' ? '\u25b2' : '\u25bc')}</span> },
    { key: 'yearOfStudy', header: <span onClick={() => handleSort('yearOfStudy')} style={{cursor: 'pointer'}}>Year of Study {sortKey === 'yearOfStudy' && (sortOrder === 'asc' ? '\u25b2' : '\u25bc')}</span> },
    { key: 'semester', header: <span onClick={() => handleSort('semester')} style={{cursor: 'pointer'}}>Semester {sortKey === 'semester' && (sortOrder === 'asc' ? '\u25b2' : '\u25bc')}</span> },
    { key: 'status', header: <span onClick={() => handleSort('status')} style={{cursor: 'pointer'}}>Status {sortKey === 'status' && (sortOrder === 'asc' ? '\u25b2' : '\u25bc')}</span> },
    { key: 'actions', header: 'Actions' }
  ];

  const renderRow = (stu, idx, pageIndex, pageSize) => (
    <>
      <td>{(pageIndex * pageSize) + idx + 1}</td>
      <td>{stu.name || ''}</td>
      <td>{stu.email || ''}</td>
      <td>{stu.registrationNumber || ''}</td>
      <td>{stu.program.name || ''}</td>
      <td>{stu.examCenter.centreName || ''}</td>
      <td>{stu.yearOfStudy ? stu.yearOfStudy.name : '-'}</td>
      <td>{stu.semester ? stu.semester.name : '-'}</td>
      <td>
        <span className={`badge ${stu.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
          {stu.status || '-'}
        </span>
      </td>
      <td>
        <button 
          className="btn btn-sm btn-primary me-2" 
          onClick={() => navigate(`/admin/students/edit/${stu.id}`)}
        >
          Edit
        </button>
        <button 
          className="btn btn-sm btn-info me-2" 
          onClick={() => navigate(`/admin/students/profile/${stu.id}`)}
        >
          Profile
        </button>
        <button 
          className="btn btn-sm btn-danger" 
          onClick={() => setConfirmDeleteStudent(stu.id)}
        >
          Delete
        </button>
      </td>
    </>
  );

  return (
    <div className="container-fluid mt-2 px-4">
      <TitleWithBack title="View All Students" />
      <div className="row mb-3">
        <div className="col-md-4 mb-9">
          <input 
            type="text" 
            name="query" 
            placeholder="Search by name,email or registration number" 
            value={filters.query}
            onChange={handleFilterChange}
            className="form-control"
          />
        </div>
        <div className="col-md-2 mb-2">
          <select name="programId" value={filters.programId} onChange={handleFilterChange} className="form-select">
            <option value="">All Programs</option>
            {programs.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="col-md-1 mb-2">
          <select name="examCentreId" value={filters.examCentreId} onChange={handleFilterChange} className="form-select">
            <option value="">All Exam Centres</option>
            {examCentres.map(ec => (
              <option key={ec.id} value={ec.id}>{ec.centreName}</option>
            ))}
          </select>
        </div>
        <div className="col-md-1 mb-2">
          <select name="yearId" value={filters.yearId} onChange={handleFilterChange} className="form-select">
            <option value="">All Years</option>
            {years.map(y => (
              <option key={y.id} value={y.id}>{y.name}</option>
            ))}
          </select>
        </div>
        <div className="col-md-1 mb-2">
          <select name="semesterId" value={filters.semesterId} onChange={handleFilterChange} className="form-select">
            <option value="">All Semesters</option>
            {semesters.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
        <div className="col-md-1 mb-2">
          <select name="status" value={filters.status} onChange={handleFilterChange} className="form-select">
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>
      {loading ? (
        <p>Loading students...</p>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : sortedStudents.length === 0 ? (
        <p>Nothing to show here</p>
      ) : (
        <PaginatedTable
          data={sortedStudents}
          columns={columns}
          renderRow={renderRow}
          rowsPerPage={50}
        />
      )}
      {tooltip && (
        <div className={`mt-3 alert ${tooltip.startsWith('Error') ? 'alert-danger' : 'alert-success'}`}>
          {tooltip}
        </div>
      )}
      {confirmDeleteStudent && (
        <ConfirmationModal 
          title="Confirm Delete"
          message="Are you sure you want to delete this student?"
          onConfirm={async () => {
            try {
              await axios.delete(`/api/students/${confirmDeleteStudent}`);
              setTooltip('Student deleted successfully!');
              fetchAllData();
              setConfirmDeleteStudent(null);
            } catch (err) {
              setTooltip('Error deleting student.');
              setConfirmDeleteStudent(null);
            }
          }}
          onCancel={() => setConfirmDeleteStudent(null)}
        />
      )}
    </div>
  );
}

export default StudentView;
